// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Button, Typography } from '@mui/material';
import {
  AppIcon,
  defineComponent,
  KpiBoxes,
  OptionsSelector,
  PageContainer,
} from '@shared/ui-kit';
import { Translation } from '@shared/ui-kit/providers/translation-provider';
import { IlliquidAssetDrawer } from './illiquid-asset-drawer';
import { IlliquidAssetDetailDrawer } from './illiquid-asset-detail-drawer';
import { Loader, RefreshControl } from '../../components';
import { IlliquidAssetsList } from './illiquid-assets-list';
import { useIliquidAssets } from './use-iliquid-assets';
import styles from './iliquid-assets.module.css';

export const IliquidAssets = defineComponent({
  displayName: 'IliquidAssets',
  useSetup: useIliquidAssets,
  View: ({
    t,
    allAssetsCount,
    assets,
    kpis,
    status,
    isRefreshing,
    addDrawerOpen,
    onAddDrawerOpen,
    onAddDrawerClose,
    onRefresh,
    tagsOptions,
    selectedTag,
    setSelectedTag,
    detailDrawerOpen,
    selectedAsset,
    onDetailDrawerOpen,
    onDetailDrawerShow,
    onDetailDrawerClose,
    tags,
    initialAssignedTags,
    onChangeTagIds,
    onCreateTag,
    onAddDrawerSubmit,
    isCreatingAsset,
    onDetailDrawerDelete,
    currency,
  }) => (
    <>
      <IlliquidAssetDrawer
        open={addDrawerOpen}
        onOpen={onAddDrawerOpen}
        onClose={onAddDrawerClose}
        onSubmit={onAddDrawerSubmit}
        isCreating={isCreatingAsset}
      />

      <IlliquidAssetDetailDrawer
        open={detailDrawerOpen}
        onClose={onDetailDrawerClose}
        asset={selectedAsset}
        tags={tags}
        initialAssignedTags={initialAssignedTags}
        onChangeTagIds={onChangeTagIds}
        onCreateTag={onCreateTag}
        onDelete={onDetailDrawerDelete}
      />

      <PageContainer
        titlePage={
          <span className={styles['page-title-wrap']}>
            <Translation i18nKey="aggregator.illiquid-assets" ns="aggregator" />
            <span className={styles['page-subtitle']}>
              <Translation
                i18nKey="aggregator.illiquid-assets-registered-count"
                ns="aggregator"
                values={{ count: allAssetsCount }}
              />
            </span>
          </span>
        }
        actionBtn={
          <div className={styles['action-bar']}>
            <div className={styles['filter-width']}>
              <OptionsSelector
                optionsAvailable={tagsOptions}
                onChangeValueSelected={setSelectedTag}
                optionSelected={selectedTag}
                defaultValue=""
                defaultText={t('aggregator.all-accounts', { ns: 'aggregator' })}
                selectType="white"
                leadingIcon="Funnel"
              />
            </div>
            <Button
              color="secondary"
              variant="contained"
              onClick={onAddDrawerOpen}
              startIcon={<AppIcon icon="Plus" color="light" size="base" />}
              className={styles['add-asset-button']}
            >
              <Translation i18nKey="aggregator.add-asset" ns="aggregator" />
            </Button>
          </div>
        }
      >
        <Loader
          status={status}
          shouldDisplayContent={assets.length > 0}
          shouldDisplayEmptyState={assets.length === 0}
          emptyStateDescription={
            <Translation
              i18nKey="aggregator.illiquid-assets-empty-message"
              ns="aggregator"
            />
          }
          onRefresh={onRefresh}
        >
          <div className={styles['kpi-grid']}>
            {kpis.map(kpi => (
              <KpiBoxes key={kpi.text} {...kpi} />
            ))}
          </div>

          <div className={styles['section-header']}>
            <div className={styles['section-header-inner']}>
              <AppIcon icon="Boxes" size="lg" color="inherit" />
              <Typography variant="h5" className={styles['section-heading']}>
                <Translation
                  i18nKey="aggregator.illiquid-assets-section"
                  ns="aggregator"
                />
              </Typography>
            </div>

            <RefreshControl
              mode="update"
              onRefresh={onRefresh}
              disabled={
                isRefreshing || status === 'pending' || allAssetsCount === 0
              }
            />
          </div>

          <IlliquidAssetsList
            assets={assets}
            onAssetClick={onDetailDrawerOpen}
            currency={currency}
          />
        </Loader>
      </PageContainer>
    </>
  ),
});
